import { Show, createSignal, onMount, createEffect } from "solid-js";
import { A } from "@solidjs/router";
import { DataTypes, useLocalStore } from "../context/StateContext";
import { cn } from "@/utilities/cn";
import Types from "@/types";

export const UserMenu = (props: { collapsed?: boolean }) => {
  const localStore = useLocalStore();
  const [editingName, setEditingName] = createSignal(false);
  const [newName, setNewName] = createSignal("");
  const [loading, setLoading] = createSignal(false);

  const showSignOutConfirm = () => {
    if (document.getElementById('user-actions')) {
      // Only close desktop menu
      document.getElementById('user-actions')?.hidePopover?.();
    }
    document.getElementById('logout-confirm')?.showPopover?.();
  };

  const copyUserId = async () => {
    if (!localStore.activeUserId) return
    await navigator.clipboard.writeText(localStore.activeUserId)
  };

  const saveName = async () => {
    if (!localStore.activeUserId || !newName() || loading()) return
    setLoading(true)
    try {
      if (localStore.activeUser?.name) {
        await localStore.updateConnection(
          localStore.activeUser.nameId,
          newName()
        )
      } else {
        await localStore.createConnection({
          origin: localStore.activeUserId,
          type: Types.Name,
          target: newName(),
          targetType: DataTypes.String
        })
      }
      setEditingName(false)
      setNewName("")
    } catch (err) {
      console.error("Failed to save name:", err)
    }
    setLoading(false)
  }

  // Helper to get display initial
  const getInitial = () => {
    if (!localStore.activeUser?.name) return 'a'
    return localStore.activeUser.name.charAt(0)
  }

  return (
    <Show
      when={localStore.activeUserId}
      fallback={
        <A
          href="/signin"
          class={cn(
            "flex items-center gap-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors",
            props.collapsed ? "p-1 w-10" : "p-3"
          )}
        >
          <svg class="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <Show when={!props.collapsed}>
            <span class="flex-1 text-sm">Sign In</span>
          </Show>
        </A>
      }
    >
      <div class="flex flex-col gap-2">
        {/* Desktop: Card with Popover */}
        <div class="@[1024px]/main:block hidden">
          <div class="relative">
            <button
              anchor="user-card"
              popovertarget="user-actions"
              class={cn("flex flex-row gap-3 items-center rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer group", props.collapsed ? "p-1 rounded-full w-auto" : "w-full p-4")}
            >
              <div class="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white group-hover:bg-blue-600">
                {getInitial()}
              </div>
              <Show when={!props.collapsed}>
                <div class="flex-1 flex-col text-left">
                  <p class="text-sm font-medium">{localStore.activeUser?.name || 'anon'}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Manage account</p>
                </div>
                <span class="text-gray-400">▼</span>
              </Show>
            </button>

            <div
              id="user-actions"
              popover="auto"
              class="absolute w-48 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
              style={{
                "inset-block-start": "anchor(top)",
                "inset-inline-start": "anchor(start)",
                "inset-inline-end": "0",
                "margin-block-start": "-8px",
                transform: "translateY(-100%)"
              }}
            >
              <Show
                when={!editingName()}
                fallback={
                  <div class="px-4 py-2">
                    <input
                      type="text"
                      value={newName()}
                      onInput={(e) => setNewName(e.currentTarget.value)}
                      placeholder="Enter name"
                      class="w-full p-1 text-sm border rounded"
                    />
                    <div class="flex gap-2 mt-2">
                      <button
                        onClick={() => setEditingName(false)}
                        class="flex-1 px-2 py-1 text-xs border rounded hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveName}
                        disabled={loading()}
                        class="flex-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                }
              >
                <button
                  onClick={() => setEditingName(true)}
                  class="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit Name
                </button>
              </Show>
              <A
                href={`/items/${localStore.activeUserId}`}
                class="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                View Profile
              </A>
              <button onClick={copyUserId} class="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy ID
              </button>
              <hr class="my-1 border-gray-200 dark:border-gray-700" />
              <button
                onClick={showSignOutConfirm}
                class="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Mobile: Card as Link + Separate Logout */}
        <div class="@[1024px]/main:hidden flex flex-col gap-2">
          <A
            href={`/items/${localStore.activeUserId}`}
            class="p-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer group"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white group-hover:bg-blue-600">
                {getInitial()}
              </div>
              <div class="@[16rem]:flex hidden flex-1 flex-col text-left">
                <span class="text-sm font-medium">{localStore.activeUser?.name || 'anon'}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400">View profile</span>
              </div>
            </div>
          </A>

          <button onClick={copyUserId} class="flex items-center gap-2 w-full p-3 text-sm rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            Copy ID
          </button>

          <button
            onClick={showSignOutConfirm}
            class="flex items-center gap-2 w-full p-3 text-sm text-red-600 dark:text-red-400 rounded-md bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950 cursor-pointer"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      </div>
    </Show>
  );
};
